// routes/moderation.tsx
// -----------------------------------------------------------------------------
// Moderation dashboard — matches mobile's moderation list screen.
// Admin-only: users not in admin_users table are redirected to /clubs.
// Shows unprocessed reports with inline content preview and approve/reject actions.
// -----------------------------------------------------------------------------

import { useState, useCallback, useEffect } from "react";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { requireAuth } from "@/lib/auth";
import {
  AppLayout,
  PageTransition,
  useHeaderConfig,
} from "@/components/layout";
import { Icon } from "@/components/ui/Icon";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/components/providers/AuthProvider";
import {
  useCheckIsAdminQuery,
  useFetchModerationReportsQuery,
  useFetchReportedItemQuery,
  useModerateActionMutation,
  type ModerationReport,
} from "@/api/clubsApi";
import { userDisplayName } from "@tinyclub/shared/utils";
import { DateTime } from "luxon";

export const Route = createFileRoute("/moderation")({
  beforeLoad: requireAuth,
  component: ModerationPage,
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatRelativeTime(dateString: string): string {
  const date = DateTime.fromISO(dateString);
  const now = DateTime.now();
  const diff = now.diff(date, ["days", "hours", "minutes"]);

  if (diff.days >= 7) {
    return date.toFormat("MMM d");
  } else if (diff.days >= 1) {
    return `${Math.floor(diff.days)}d`;
  } else if (diff.hours >= 1) {
    return `${Math.floor(diff.hours)}h`;
  } else if (diff.minutes >= 1) {
    return `${Math.floor(diff.minutes)}m`;
  }
  return "now";
}

const itemTypeLabels: Record<string, string> = {
  post: "Post",
  comment: "Comment",
  club: "Club",
  user: "User",
};

const itemTypeIcons: Record<string, string> = {
  post: "file-lines",
  comment: "comment",
  club: "users",
  user: "user",
};

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

function ModerationPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedReportId, setSelectedReportId] = useState<number | null>(null);
  const [showProcessed, setShowProcessed] = useState(false);

  // Admin check
  const { data: isAdmin, isLoading: isCheckingAdmin } = useCheckIsAdminQuery(
    user?.id ?? "",
    { skip: !user?.id }
  );

  // Redirect non-admins
  useEffect(() => {
    if (user?.id && !isCheckingAdmin && isAdmin === false) {
      navigate({ to: "/clubs" });
    }
  }, [user?.id, isCheckingAdmin, isAdmin, navigate]);

  // Fetch reports
  const {
    data: reports,
    isLoading: isLoadingReports,
    refetch,
  } = useFetchModerationReportsQuery(
    { processed: showProcessed },
    { skip: !isAdmin, pollingInterval: 30_000 }
  );

  // Header
  useHeaderConfig(
    {
      breadcrumbs: [{ label: "Moderation" }],
      actions: (
        <button
          onClick={() => {
            setShowProcessed((v) => !v);
            setSelectedReportId(null);
          }}
          className="text-body-2-medium text-text-secondary hover:text-text-primary transition-colors"
        >
          {showProcessed ? "Show pending" : "Show processed"}
        </button>
      ),
    },
    [showProcessed]
  );

  // Handle action completion — deselect and refetch
  const handleActionComplete = useCallback(() => {
    setSelectedReportId(null);
  }, []);

  // Loading / not-admin states
  if (isCheckingAdmin || !isAdmin) {
    return (
      <AppLayout>
        <PageTransition>
          <div className="flex items-center justify-center h-[60vh]">
            <Skeleton className="h-8 w-48" />
          </div>
        </PageTransition>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageTransition>
        <div className="p-6 pb-24">
          <div className="max-w-2xl mx-auto">
            {isLoadingReports ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-20 w-full rounded-2xl" />
                ))}
              </div>
            ) : !reports || reports.length === 0 ? (
              /* Empty state — matches mobile */
              <div className="flex flex-col items-center pt-24">
                <Icon
                  name="shield-check"
                  size={40}
                  className="text-text-tertiary"
                />
                <p className="text-body-1 text-text-tertiary mt-4">
                  {showProcessed
                    ? "No processed reports"
                    : "No pending reports"}
                </p>
              </div>
            ) : (
              <div className="bg-surface-1 rounded-2xl overflow-hidden">
                {reports.map((report, index) => (
                  <div key={report.id}>
                    {index > 0 && (
                      <div className="h-px bg-border-subtle mx-4" />
                    )}
                    <ReportRow
                      report={report}
                      isSelected={selectedReportId === report.id}
                      onSelect={() =>
                        setSelectedReportId(
                          selectedReportId === report.id ? null : report.id
                        )
                      }
                      onActionComplete={handleActionComplete}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </PageTransition>
    </AppLayout>
  );
}

// ---------------------------------------------------------------------------
// Report row — list item + expandable detail
// ---------------------------------------------------------------------------

function ReportRow({
  report,
  isSelected,
  onSelect,
  onActionComplete,
}: {
  report: ModerationReport;
  isSelected: boolean;
  onSelect: () => void;
  onActionComplete: () => void;
}) {
  const iconName = itemTypeIcons[report.item_type] || "flag";
  const typeLabel = itemTypeLabels[report.item_type] || report.item_type;
  const reporterName = report.reporter
    ? userDisplayName(report.reporter as any)
    : "Unknown";

  return (
    <div>
      {/* Clickable row — matches mobile's report item layout */}
      <button
        className="flex items-start gap-3 px-4 py-3 w-full text-left hover:bg-surface-2 transition-colors cursor-pointer"
        onClick={onSelect}
      >
        {/* Item type icon */}
        <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
          <Icon name={iconName} size={24} className="text-text-primary" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="text-body-1-medium text-text-primary">
            {typeLabel} report
          </p>
          <p className="text-body-3 text-text-tertiary mt-0.5">
            {formatRelativeTime(report.created_at)}
            {" · "}
            Reported by {reporterName}
          </p>
          {report.reason && (
            <p className="text-body-2 text-text-secondary mt-1 line-clamp-2">
              {report.reason}
            </p>
          )}
        </div>

        {/* Chevron */}
        <div className="flex items-center h-10 flex-shrink-0">
          <Icon
            name={isSelected ? "angle-down" : "angle-right"}
            size={16}
            className="text-text-secondary"
          />
        </div>
      </button>

      {/* Expanded detail */}
      {isSelected && (
        <ReportDetail report={report} onActionComplete={onActionComplete} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Report detail — content preview + approve/reject actions
// ---------------------------------------------------------------------------

function ReportDetail({
  report,
  onActionComplete,
}: {
  report: ModerationReport;
  onActionComplete: () => void;
}) {
  const { showToast } = useToast();
  const [moderateAction] = useModerateActionMutation();
  const [isActing, setIsActing] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);

  // Fetch reported item content
  const { data: itemData, isLoading: isLoadingItem } =
    useFetchReportedItemQuery({
      itemType: report.item_type,
      itemId: report.item_id,
    });

  const isUser = report.item_type === "user";

  const handleApprove = async () => {
    if (isActing) return;
    setIsActing(true);
    try {
      await moderateAction({
        reportId: report.id,
        itemType: report.item_type,
        itemId: report.item_id,
        action: "approve",
      }).unwrap();
      showToast("Report approved", "success");
      onActionComplete();
    } catch {
      showToast("Failed to approve. Please try again.", "error");
    } finally {
      setIsActing(false);
    }
  };

  const handleReject = async () => {
    if (isActing) return;
    setIsActing(true);
    try {
      await moderateAction({
        reportId: report.id,
        itemType: report.item_type,
        itemId: report.item_id,
        action: "reject",
      }).unwrap();
      showToast(
        isUser ? "User banned" : "Item rejected",
        "success"
      );
      onActionComplete();
    } catch {
      showToast("Failed to reject. Please try again.", "error");
    } finally {
      setIsActing(false);
      setShowRejectDialog(false);
    }
  };

  // Build link to view content in context
  const contentLink = itemData?.clubId
    ? report.item_type === "post"
      ? `/clubs/${itemData.clubId}`
      : report.item_type === "comment"
        ? `/clubs/${itemData.clubId}`
        : report.item_type === "club"
          ? `/clubs/${itemData.clubId}`
          : `/profile/${report.item_id}`
    : report.item_type === "user"
      ? `/profile/${report.item_id}`
      : null;

  return (
    <div className="px-4 pb-4 ml-[52px]">
      {/* Content preview card */}
      <div className="bg-surface-2 rounded-xl p-4 mb-4">
        {isLoadingItem ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-full" />
          </div>
        ) : itemData ? (
          <>
            {itemData.author && (
              <p className="text-body-2-medium text-text-primary mb-1">
                {itemData.author}
                {itemData.clubName && (
                  <span className="text-text-tertiary font-normal">
                    {" "}
                    in {itemData.clubName}
                  </span>
                )}
              </p>
            )}
            {itemData.content && (
              <p className="text-body-2 text-text-secondary whitespace-pre-wrap line-clamp-6">
                {itemData.content}
              </p>
            )}
            {contentLink && (
              <Link
                to={contentLink}
                className="text-body-3 text-text-tertiary hover:text-text-secondary mt-2 inline-block"
              >
                View in context →
              </Link>
            )}
          </>
        ) : (
          <p className="text-body-2 text-text-tertiary">
            Content not found — it may have been deleted.
          </p>
        )}
      </div>

      {/* Action buttons — matches mobile ModerationBar layout */}
      {!report.processed && (
        <div className="flex gap-3">
          <Button
            className="flex-1 rounded-full"
            onClick={handleApprove}
            disabled={isActing}
          >
            {isActing ? "..." : "Approve"}
          </Button>
          <Button
            variant="destructive"
            className="flex-1 rounded-full"
            onClick={() => setShowRejectDialog(true)}
            disabled={isActing}
          >
            {isUser ? "Ban" : "Reject"}
          </Button>
        </div>
      )}

      {/* Reject confirmation dialog */}
      <AlertDialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isUser ? "Ban this user?" : "Reject item?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isUser
                ? "Ban this user? They will no longer be able to log in with this email."
                : "Reject this item? It will be hidden from the app and marked as failing moderation."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isActing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReject}
              disabled={isActing}
              className="bg-red text-white hover:bg-red/90"
            >
              {isActing
                ? "..."
                : isUser
                  ? "Ban"
                  : "Reject"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
